import {Bindings} from "data/constants/bindings";
import {RoundStatus} from "data/enums";
import {IPickResponse} from "data/providers/api/pinpoint.api.provider";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IPinpointStore} from "data/stores/pinpoint/pinpoint.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";

interface IProps {
	roundId: number | null;
}

export interface IRoundController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get myPick(): IPickResponse | null;
	get isEdit(): boolean;
	get isActive(): boolean;
}

@injectable()
export class RoundController implements IRoundController {
	@observable _roundId?: number | null;

	constructor(
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.PinpointStore) private _pinpointStore: IPinpointStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	get myPick() {
		if (!this._roundId) {
			return null;
		}

		return this._pinpointStore.getPickByRoundId(this._roundId);
	}

	get round() {
		if (!this._roundId) {
			return null;
		}

		return this._roundStore.getRoundById(this._roundId);
	}

	get isEdit() {
		return Boolean(this.myPick?.id) && this.round?.pinpointStatus === RoundStatus.Scheduled;
	}

	get isActive() {
		return [RoundStatus.Playing, RoundStatus.Complete].includes(
			this.round?.pinpointStatus || RoundStatus.Scheduled
		);
	}

	@action init({roundId}: IProps) {
		this._roundId = roundId;
	}
}
